import { useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import { Copy } from "lucide-react"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/modules/shared/ui/components/card"
import { Button } from "@/modules/shared/ui/components/button"
import { Badge } from "@/modules/shared/ui/components/badge"
import { Spinner } from "@/modules/shared/ui/components/spinner"
import { Input } from "@/modules/shared/ui/components/input"
import { useOrganizationsService } from "@/modules/organizations/hooks/use-organizations"
import { marketplaceApi } from "../../infrastructure/marketplace.api"
import type {
  PluginInstallDto,
  PluginSummaryDto,
  MarketplaceConnectorStatusMap,
} from "../../dtos/marketplace.dto"
import { PluginInstallWizard, type SubmitOutcome } from "../components/PluginInstallWizard"
import { usePluginInstalls } from "../../hooks/usePluginInstalls"

const apiErrorMessage = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback

type PanelState =
  | { kind: "install"; plugin: PluginSummaryDto }
  | { kind: "reconfigure"; plugin: PluginSummaryDto; install: PluginInstallDto }
  | null

export default function MarketplacePage() {
  const { currentOrganization } = useOrganizationsService()
  const organizationId = currentOrganization?.id
  // Shared installs state (also drives the sidebar plugin nav). Kept in sync
  // after every mutation so the nav reflects installs/uninstalls immediately.
  const { refresh: refreshSharedInstalls } = usePluginInstalls()

  const [plugins, setPlugins] = useState<PluginSummaryDto[]>([])
  const [installs, setInstalls] = useState<PluginInstallDto[]>([])
  const [connectorStatuses, setConnectorStatuses] = useState<MarketplaceConnectorStatusMap>({})
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [panel, setPanel] = useState<PanelState>(null)

  // silent = refresh data without flipping the full-page loading state. A
  // mutation (install/reconfigure/uninstall) refetches while the wizard dialog
  // is still open; showing the page-level spinner would unmount the wizard
  // mid-flow and remount it fresh at step 0, so those refreshes stay silent.
  const loadData = useCallback(async (silent = false) => {
    if (!organizationId) return
    if (!silent) setIsLoading(true)
    try {
      const [pluginList, installList, connectorStatusMap] = await Promise.all([
        marketplaceApi.listPlugins(),
        marketplaceApi.listInstalls(organizationId),
        marketplaceApi.getConnectorStatuses(organizationId),
      ])
      setPlugins(pluginList)
      setInstalls(installList)
      setConnectorStatuses(connectorStatusMap)
    } catch {
      toast.error("Failed to load the marketplace. Please try again later.")
    } finally {
      if (!silent) setIsLoading(false)
    }
  }, [organizationId])

  useEffect(() => {
    loadData()
  }, [loadData])

  const families = useMemo(() => {
    const groups = new Map<string, PluginSummaryDto[]>()

    for (const plugin of plugins) {
      const key = plugin.familyId ?? plugin.pluginId
      const group = groups.get(key) ?? []
      group.push(plugin)
      groups.set(key, group)
    }

    return [...groups.values()].map((tiers) => tiers.sort((a, b) => a.tier - b.tier))
  }, [plugins])

  const findActiveInstall = (tiers: PluginSummaryDto[]): PluginInstallDto | undefined =>
    installs.find(
      (i) => i.status !== "Suspended" && tiers.some((p) => p.pluginId === i.pluginId),
    )

  const closePanel = () => setPanel(null)

  const handleCopyWebhookUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      toast.success("Webhook callback URL copied.")
    } catch {
      toast.error("Couldn't copy to clipboard. Select and copy the URL manually.")
    }
  }

  const handleInstall = async (
    plugin: PluginSummaryDto,
    values: Record<string, string>,
  ): Promise<SubmitOutcome> => {
    if (!organizationId) return { ok: false, error: "No organization selected." }
    setIsSubmitting(true)
    try {
      const result = await marketplaceApi.installPlugin(organizationId, {
        pluginVersionId: plugin.pluginVersionId,
        configValues: values,
      })
      // The command returns 200 even when activation fails — the outcome is in
      // the status. Only a genuinely active install is a success; anything else
      // is handed back to the wizard so the user can fix and retry in place.
      if (result.status !== "Active")
        return { ok: false, error: result.lastError ?? "We couldn't activate this plugin." }

      await Promise.all([loadData(true), refreshSharedInstalls()])
      return { ok: true }
    } catch (err) {
      return { ok: false, error: apiErrorMessage(err, `Failed to install ${plugin.name}. Please try again.`) }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleReconfigure = async (
    plugin: PluginSummaryDto,
    install: PluginInstallDto,
    values: Record<string, string>,
  ): Promise<SubmitOutcome> => {
    if (!organizationId) return { ok: false, error: "No organization selected." }
    setIsSubmitting(true)
    try {
      const result = await marketplaceApi.updatePluginInstall(organizationId, install.installId, {
        pluginVersionId: plugin.pluginVersionId,
        configValues: values,
      })
      if (result.status !== "Active")
        return { ok: false, error: result.lastError ?? "We couldn't activate this plugin." }

      await Promise.all([loadData(true), refreshSharedInstalls()])
      return { ok: true }
    } catch (err) {
      return { ok: false, error: apiErrorMessage(err, `Failed to update ${plugin.name}. Please try again.`) }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleUninstall = async (install: PluginInstallDto) => {
    if (!organizationId) return
    setIsSubmitting(true)
    try {
      await marketplaceApi.uninstallPlugin(organizationId, install.installId)
      toast.success(`${install.pluginName} uninstalled.`)
      await Promise.all([loadData(true), refreshSharedInstalls()])
    } catch {
      toast.error(`Failed to uninstall ${install.pluginName}.`)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto py-16 flex items-center justify-center gap-2 text-muted-foreground">
        <Spinner className="w-4 h-4 animate-spin" />
        Loading marketplace…
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Marketplace</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Add plugins and add-ons to extend Tempered Talent. Install, upgrade, or remove them at any time.
        </p>
      </div>

      {families.map((tiers) => {
        const activeInstall = findActiveInstall(tiers)
        const activePlugin = activeInstall
          ? tiers.find((p) => p.pluginId === activeInstall.pluginId)
          : undefined
        const familyKey = tiers[0].familyId ?? tiers[0].pluginId

        return (
          <Card key={familyKey} className="bg-card border border-border shadow-sm">
            <CardHeader className="flex flex-row items-start justify-between gap-2">
              <CardTitle className="text-xl flex items-center gap-2">
                {tiers[0].familyId ? tiers[0].name.replace(/\+$/, "") : tiers[0].name}
                {activeInstall?.status === "Active" && (
                  <Badge variant="default">Active</Badge>
                )}
              </CardTitle>
              {activeInstall && (
                <button
                  type="button"
                  onClick={() => handleUninstall(activeInstall)}
                  disabled={isSubmitting}
                  className="shrink-0 text-sm text-muted-foreground underline-offset-4 hover:text-destructive hover:underline disabled:pointer-events-none disabled:opacity-50"
                >
                  Uninstall {activeInstall.pluginName}
                </button>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {activeInstall?.status === "Active" && activeInstall.webhookCallbackUrl && (
                <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-1.5">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Webhook Callback URL
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Paste this into Meta's WhatsApp app under Configuration → Webhooks.
                  </p>
                  <div className="flex items-center gap-2">
                    <Input
                      readOnly
                      value={activeInstall.webhookCallbackUrl}
                      onFocus={(e) => e.currentTarget.select()}
                      className="font-mono text-xs h-8"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="shrink-0"
                      onClick={() => handleCopyWebhookUrl(activeInstall.webhookCallbackUrl!)}
                    >
                      <Copy className="w-3.5 h-3.5" />
                      Copy
                    </Button>
                  </div>
                </div>
              )}

              {tiers.length === 1 ? (
                <p className="text-sm text-muted-foreground">{tiers[0].description}</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {tiers.map((tier) => {
                    const isCurrentTier = activePlugin?.pluginId === tier.pluginId
                    return (
                      <div
                        key={tier.pluginId}
                        className={`rounded-lg border-2 p-4 ${isCurrentTier ? "border-primary bg-primary/5" : "border-border"}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-semibold">{tier.name}</h3>
                          {isCurrentTier && (
                            <Badge variant="default">Current tier</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">{tier.description}</p>
                        {isCurrentTier ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              activeInstall &&
                              setPanel({ kind: "reconfigure", plugin: tier, install: activeInstall })
                            }
                          >
                            Reconfigure
                          </Button>
                        ) : activeInstall ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setPanel({ kind: "reconfigure", plugin: tier, install: activeInstall })
                            }
                          >
                            {tier.tier > (activePlugin?.tier ?? 0) ? "Upgrade" : "Downgrade"} to {tier.name}
                          </Button>
                        ) : (
                          <Button size="sm" onClick={() => setPanel({ kind: "install", plugin: tier })}>
                            Install
                          </Button>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}

              {tiers.length === 1 && (
                <div>
                  {activeInstall ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setPanel({ kind: "reconfigure", plugin: tiers[0], install: activeInstall })
                      }
                    >
                      Reconfigure
                    </Button>
                  ) : (
                    <Button size="sm" onClick={() => setPanel({ kind: "install", plugin: tiers[0] })}>
                      Install
                    </Button>
                  )}
                </div>
              )}

            </CardContent>
          </Card>
        )
      })}

      {panel && (
        <PluginInstallWizard
          mode={panel.kind}
          plugin={panel.plugin}
          connectorStatuses={connectorStatuses}
          initialValues={panel.kind === "reconfigure" ? panel.install.configValues : undefined}
          isSubmitting={isSubmitting}
          onClose={closePanel}
          onSubmit={(values) =>
            panel.kind === "install"
              ? handleInstall(panel.plugin, values)
              : handleReconfigure(panel.plugin, panel.install, values)
          }
        />
      )}
    </div>
  )
}
